<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Globe, Check, ChevronDown } from 'lucide-vue-next';
import type { SupportedLocale } from '@/i18n';

const { locale } = useI18n();
const isOpen = ref(false);

const languages: Array<{ code: SupportedLocale; label: string; short: string }> = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'ja', label: '日本語 (Japanese)', short: '日' },
  { code: 'zh-TW', label: '繁體中文 (Traditional Chinese)', short: '繁中' },
  { code: 'ko', label: '한국어 (Korean)', short: '한국어' },
];

const currentLang = computed(() => {
  return languages.find(l => l.code === locale.value) || languages[0];
});

const selectLanguage = (code: SupportedLocale) => {
  locale.value = code;
  localStorage.setItem('nbc_locale', code);
  isOpen.value = false;
};
</script>

<template>
  <div class="relative inline-block text-left">
    <!-- Trigger Button -->
    <button
      type="button"
      @click="isOpen = !isOpen"
      class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-800 hover:border-slate-700 text-xs font-bold text-slate-200 transition shadow-sm"
      :title="currentLang.label"
    >
      <Globe class="w-3.5 h-3.5 text-sky-400" />
      <span class="text-xs">{{ currentLang.short }}</span>
      <ChevronDown class="w-3 h-3 text-slate-400 transition-transform" :class="isOpen ? 'rotate-180' : ''" />
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-2 w-48 bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100"
      style="background-color: #0f172a;"
    >
      <button
        v-for="lang in languages"
        :key="lang.code"
        type="button"
        @click="selectLanguage(lang.code)"
        class="w-full flex items-center justify-between px-3.5 py-2 hover:bg-slate-800 transition text-left text-xs font-semibold"
        :class="locale === lang.code ? 'text-sky-400 bg-slate-800/60' : 'text-slate-300 hover:text-slate-100'"
      >
        <span>{{ lang.label }}</span>
        <Check v-if="locale === lang.code" class="w-3.5 h-3.5 text-sky-400" />
      </button>
    </div>

    <!-- Backdrop dismiss overlay -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="isOpen = false" />
  </div>
</template>